/* eslint-disable max-len */
import { Node, mergeAttributes } from '@tiptap/core';
import { isEmpty } from '../../helpers/common';

export interface TaskLinkEmbedOptions {
  HTMLAttributes?: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    taskLinkEmbed: {
      setTaskLinkEmbed: (options: { url: string; taskId: string; taskName: string; themeMode?: string }) => ReturnType;
    };
  }
}

export const TaskLinkEmbed = Node.create<TaskLinkEmbedOptions>({
  name: 'taskLinkEmbed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      url: { default: '' },
      taskId: { default: '' },
      taskName: { default: '' },
      priorityName: { default: '' },
      projectName: { default: '' },
      statusName: { default: '' },
      assigneeName: { default: '' },
      themeMode: { default: 'light' },
      assigneeUrls: { default: '' },
      statusUrl: { default: '' },
      priorityUrl: { default: '' },
      showPriority: { default: false }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.task-link-preview-main-div',
        getAttrs: (dom: HTMLElement) => {
          return {
            url: dom.getAttribute('url'),
            taskId: dom.getAttribute('name'),
            taskName: dom.getAttribute('taskName'),
            priorityName: dom.getAttribute('priorityName'),
            projectName: dom.getAttribute('projectName'),
            statusName: dom.getAttribute('statusName'),
            assigneeName: dom.getAttribute('assigneeName'),
            themeMode: dom.getAttribute('themeMode'),
            assigneeUrls: dom.getAttribute('assigneeUrls'),
            priorityUrl: dom.getAttribute('priorityUrl'),
            statusUrl: dom.getAttribute('statusUrl'),
            showPriority: dom.getAttribute('showPriority')
          };
        }
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const {
      taskName,
      priorityName,
      projectName,
      statusName,
      assigneeName,
      assigneeUrls,
      priorityUrl,
      statusUrl,
      showPriority
    } = HTMLAttributes;

    // Split the comma-separated strings into arrays
    const assigneeUrlArray = assigneeUrls?.split(',').filter((url: string) => url.trim()) || [];
    const assigneeNameArray = assigneeName?.split(',') || [];

    // Create an array of image elements for all assignees
    const assigneeImages = assigneeUrlArray.map((url: string, index: number) => [
      'img',
      {
        src: url || 'https://randomuser.me/api/portraits/men/1.jpg',
        alt: assigneeNameArray[index] || 'Assignee',
        class: 'assignee-avatar embed-icon task-avtar',
        width: '14',
        height: '14',
        style: index === 0 ? '' : 'margin-left: -12px;'
      }
    ]);
    if (assigneeUrlArray.length === 1 && !isEmpty(assigneeUrlArray[0])) {
      assigneeImages.push(['span', { class: 'status-name' }, assigneeName || 'Unknown']);
    }

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        class: 'task-link-preview-main-div',
        url: HTMLAttributes.url,
        name: HTMLAttributes?.taskId,
        taskName: HTMLAttributes?.taskName,
        priorityName: HTMLAttributes?.priorityName,
        projectName: HTMLAttributes?.projectName,
        statusName: HTMLAttributes?.statusName,
        assigneeName: HTMLAttributes?.assigneeName,
        themeMode: HTMLAttributes?.themeMode,
        assigneeUrls: assigneeUrls
      }),
      [
        'div',
        { class: 'task-link-container' },
        [
          'div',
          { class: 'task-link-content' },
          [
            'div',
            { class: 'task-link-wrapper' },
            ['div', { class: 'task-project-name' }, projectName || 'TeamCamp Web'],
            ['div', { class: 'task-title' }, taskName || 'Task Link']
          ]
        ],
        [
          'div',
          { class: 'task-tab-section' },
          // Status
          [
            'div',
            { class: 'task-status task-assignee' },
            [
              'img',
              {
                src: statusUrl || 'https://randomuser.me/api/portraits/men/1.jpg',
                alt: statusName || 'Status',
                class: 'embed-icon',
                width: '14',
                height: '14'
              }
            ],
            ['span', { class: 'status-name' }, statusName || 'TODO']
          ],
          // Priority
          ...(showPriority
            ? [
                [
                  'div',
                  { class: 'task-status task-assignee' },
                  [
                    'img',
                    {
                      src: priorityUrl || 'https://randomuser.me/api/portraits/men/1.jpg',
                      alt: priorityName || 'Priority',
                      class: 'embed-icon',
                      width: '14',
                      height: '14'
                    }
                  ],
                  ['span', { class: 'status-name' }, priorityName || 'No Priority']
                ]
              ]
            : []),
          // Assignee
          [
            'div',
            { class: 'task-status task-assignee task-avtar' },
            ...assigneeImages // Spread the array of image elements
          ]
        ]
      ]
    ];
  },

  addCommands() {
    return {
      setTaskLinkEmbed:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options
          });
        }
    };
  }
});
